// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { removeNullAndUndefinedProps } from "@transformers/object";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreator from "@modules/service/actionCreator";
import { Col, Row, Button } from "@components";
import type { StepProps } from "@components";
import { AbsenceMonitoringSettingsForm } from "@modules/service/components";
import { split } from "@transformers/schema";
import type { AbsenceMonitoring } from "@modules/service/reducer/absence-monitoring/types";

type OwnProps = $Exact<{
  editing: boolean,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?AbsenceMonitoring,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetAlertDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepSettings extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    // Class components needs constructor for accessing the SchemaForm's ref
    this.formRef = React.createRef();
  }
  validateSettings: *;

  componentDidMount() {
    this.validateSettings = new Ajv().compile(this.schema.settings);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.schema && this.props.schema !== prevProps.schema) {
      this.validateSettings = new Ajv().compile(this.schema.settings);
    }
  }

  get data() {
    const draft = this.props.draft;
    return {
      settings: {
        id: draft?.id || null,
        name: draft?.content?.name || null,
        description: draft?.content?.description || null,
        type: "STREAMLINED",
        activeFrom: draft?.content?.activeFrom || null,
        activeTo: draft?.content?.activeTo || null,
      },
    };
  }

  get schema() {
    return {
      settings: split(this.props.schema, Object.keys(this.data.settings)),
    };
  }

  isValid = (data?: Object) => {
    if (!this.validateSettings || !data) {
      return false;
    }

    if (data.settings) {
      const fromDate = data.settings.activeFrom
        ? new Date(data.settings.activeFrom).getTime()
        : false;
      const toDate = data.settings.activeTo ? new Date(data.settings.activeTo).getTime() : false;

      if (fromDate && toDate && fromDate > toDate) {
        return false;
      }
    }

    const isValid = this.validateSettings(removeNullAndUndefinedProps(data.settings));
    if (!isValid) {
      console.warn("isValid errors", this.validateSettings.errors);
    }

    return isValid;
  };

  submitForm = () => {
    this.formRef && this.formRef.current.submit();
  };

  submitSettings = (formData: Object) => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit(this.data, true);
    }
  };

  updateSettings = (formData: Object) => {
    const next = {
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        ...formData,
      },
    };
    this.props.setDraft(next);
  };

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <React.Fragment>
        <Row className="mt-2 w-100 d-flex justify-content-center">
          <Col sm="12">
            {this.schema.settings && this.data ? (
              <AbsenceMonitoringSettingsForm
                formRef={this.formRef}
                data={this.data.settings}
                schema={this.schema.settings}
                onChange={this.updateSettings}
                onSubmit={this.submitSettings}
                editing={this.props.editing}
              />
            ) : null}
          </Col>
        </Row>
        {this.props.editing ? null : (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button onClick={this.submitForm} disabled={!this.isValid(this.data)}>
              {this.props.t("ABSENCE_MONITORING_STEP1_NEXT_BUTTON")}
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: serviceSelectors.getAbsenceMonitoringSchema(state),
    draft: serviceSelectors.getAbsenceMonitoringDraft(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetAbsenceMonitoringDraft,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(StepSettings);
